import cachios from 'cachios';
import get from 'lodash/get';
import hasher from 'node-object-hash';
import { SESSION_COOKIE_NAME } from '@/constants/global';

const hash = hasher();

export default (context, inject) => {
  let redis = null;
  const {
    $axios, $cookies, store, route,
  } = context;
  const { preview_campaign: previewCampaign } = route.query;

  // if we are passed a preview campaign, pass it on to axios calls
  if (previewCampaign) {
    $axios.interceptors.request.use((config) => {
      config.params = config.params || {}; // eslint-disable-line no-param-reassign
      config.params.preview_campaign = previewCampaign; // eslint-disable-line no-param-reassign

      return config;
    });
  }

  if (context.ssrContext) {
    const cookies = $cookies.getAll();
    const sessionId = cookies[SESSION_COOKIE_NAME] || cookies.sessionid;

    if (sessionId) {
      $axios.defaults.headers.common.Cookie = `${SESSION_COOKIE_NAME}=${sessionId}`;
    }
  }

  if (process.browser || get(store, 'state.session.session.disable_cachios')) {
    // Cachios not supported on client
    // Just default back to $axios whenever it's used
    inject('cachios', $axios);
    context.$cachios = $axios;
    return;
  }

  if (process.server && process.env.JEST_WORKER_ID === undefined) {
    // run on server only
    redis = context.ssrContext.$redis;
  }

  const cachiosInstance = cachios.create($axios, {
    stdTTL: 300,
  });

  if (redis && redis.status === 'ready') {
    cachiosInstance.request = async function request(config) {
      const { ttl } = config;
      const force = config.force || false;
      const cacheKey = this.getCacheKey(config);
      const cachedValue = await this.getCachedValue(cacheKey);

      // if we find a cached value, return it immediately
      if (cachedValue !== undefined && force !== true) {
        return Promise.resolve(cachedValue);
      }

      // if we find a staging promise (a request that has not
      // yet completed, so it is not yet in cache), return it.
      if (this.stagingPromises[cacheKey]) {
        return this.stagingPromises[cacheKey];
      }

      // otherwise, send a real request and cache the value for later
      const me = this;
      const pendingPromise = this.axiosInstance.request(config);

      // store the promise in stagingPromises so it can be used before completing
      // we don't store it in the cache immediately because:
      // - we don't want it in the cache if the request fails
      // - our cache backend may not support promises
      this.stagingPromises[cacheKey] = pendingPromise;

      // once the request successfully copmletes, store it in cache
      pendingPromise.then((resp) => {
        me.setCachedValue(cacheKey, me.getResponseCopy(resp), ttl);
      }).catch(() => undefined).then(() => { // undefined is intentional (sonarcube error)
        // always delete the staging promise once the request is complete
        // (finished or failed)
        delete me.stagingPromises[cacheKey];
      });
      // return the original promise
      return pendingPromise;
    };

    cachiosInstance.getCacheIdentifier = (config) => {
      const { user } = context.store.state.session;
      const key = {
        method: config.method,
        url: config.url,
        params: config.params,
        data: config.data,
        is_authenticated: false,
      };

      // things that affect include_groups
      if (user) {
        const { preview_campaign: activePreviewCampaign } = context.store.state.session?.session;

        Object.assign(key, {
          is_authenticated: true,
          has_plus: !!user.has_plus,
          has_briefcase: !!user.has_briefcase,
          is_staff: !!user.is_staff,
          is_betaling: !!user.is_betaling,
        });

        if (activePreviewCampaign && activePreviewCampaign.slug) {
          Object.assign(key, {
            preview_campaign: activePreviewCampaign.slug,
          });
        }
      }

      return key;
    };

    cachiosInstance.getCacheKey = (config) => hash.hash(cachiosInstance.getCacheIdentifier(config));

    cachiosInstance.cache = {
      async get(cacheKey) {
        const val = await redis.get(`cachios.${cacheKey}`);

        if (val) {
          try {
            return JSON.parse(val);
          } catch (ex) {
            console.log('Failed to parse JSON:', ex.message); // eslint-disable-line no-console
          }
        }

        return undefined;
      },

      async set(cacheKey, cacheValue, ttl = 300) {
        // store in redis cache
        await redis.set(`cachios.${cacheKey}`, JSON.stringify(cacheValue), 'EX', ttl);
      },
    };
  }

  inject('cachios', cachiosInstance);
  context.$cachios = cachiosInstance;
};
